import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import UiIcon from '../components/UiIcon';
import AmvTablePager from '../components/admin/AmvTablePager';
import { API_BASE_URL } from '../config/env';
import { fetchAtencionBotProductos } from '../api/atencionBot';
import { buildCrmSwalBody, fireConfirmDialog, fireCrmAlert } from '../utils/swalConfirm';
import '../styles/admin-medicos-visibilidad-premium.css';
import '../styles/bot-farmacia-promociones-premium.css';

const ENDPOINTS = {
  list: '/api/asociacion-recetas/promociones-productos',
  lookup: (idProducto) => `/api/asociacion-recetas/promociones-productos/lookup/${encodeURIComponent(idProducto)}`,
  create: '/api/asociacion-recetas/promociones-productos',
  update: (id) => `/api/asociacion-recetas/promociones-productos/${id}`,
  delete: (id) => `/api/asociacion-recetas/promociones-productos/${id}`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const EMPTY_DRAFT = {
  idproducto: '',
  cod_barras: '',
  producto: '',
  presentacion: '',
  laboratorio: '',
  dto_peuma: '',
  precio: '',
  precio_descuento: '',
  is_active: 0
};

function showToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function clean(value) {
  return String(value ?? '').trim();
}

function formatCurrency(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '—';
  const normalized = raw.includes(',') && raw.includes('.')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(',', '.');
  const num = Number(normalized);
  if (!Number.isFinite(num) || num <= 0) return '—';
  return num.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function parseAmount(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return NaN;
  if (raw.includes(',') && raw.includes('.')) {
    return Number(raw.replace(/\./g, '').replace(',', '.'));
  }
  return Number(raw.replace(',', '.'));
}

function formatAmountInput(value) {
  const num = parseAmount(value);
  if (!Number.isFinite(num) || num <= 0) return clean(value);
  return num.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function normalizeBotCatalogHit(row) {
  if (!row) return null;
  return {
    idproducto: clean(row.idproducto ?? row.id_producto ?? row.codplex ?? row.id),
    cod_barras: clean(row.cod_barras ?? row.codbarra ?? row.barcode ?? row.codigo_barras),
    producto: clean(row.producto ?? row.nombre ?? row.descripcion ?? row.nombre_producto),
    presentacion: clean(row.presentacion ?? row.present ?? row.forma ?? row.descripcion_presentacion),
    laboratorio: clean(row.laboratorio ?? row.nombrelab ?? row.lab ?? row.laboratorio_nombre),
    dto_peuma: clean(row.dto_peuma ?? row.dto ?? row.descuento ?? ''),
    precio: clean(row.precio ?? row.price ?? row.precio_lista ?? ''),
    precio_descuento: clean(row.precio_descuento ?? row.precio_con_descuento ?? ''),
    raw: row
  };
}

function buildSearchVariants(query) {
  const raw = clean(query);
  if (!raw) return [];
  const normalized = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const variants = [raw];
  if (normalized && normalized !== raw.toLowerCase()) variants.push(normalized);
  const tokens = normalized.split(' ').filter((t) => t.length >= 3);
  if (tokens.length > 1) variants.push(tokens[0]);
  if (tokens.length > 2) variants.push(tokens.slice(0, 2).join(' '));
  return [...new Set(variants.filter(Boolean))];
}

function scoreCatalogHit(query, hit) {
  const q = clean(query).toLowerCase();
  if (!q || !hit) return 0;
  const fields = [
    hit.idproducto,
    hit.cod_barras,
    hit.producto,
    hit.presentacion,
    hit.laboratorio,
    hit.dto_peuma
  ].map((v) => clean(v).toLowerCase());
  if (fields.some((v) => v === q)) return 100;
  if (fields.some((v) => v.startsWith(q))) return 80;
  if (fields.some((v) => v.includes(q))) return 60;
  return 0;
}

function pickBestCatalogHit(query, hits) {
  const list = Array.isArray(hits) ? hits.map(normalizeBotCatalogHit).filter(Boolean) : [];
  if (!list.length) return null;
  const ranked = list
    .map((hit) => ({ hit, score: scoreCatalogHit(query, hit) }))
    .sort((a, b) => b.score - a.score);
  return ranked[0]?.hit || list[0];
}

function sortPromos(list) {
  return [...list].sort((a, b) => {
    const aKey = clean(a?.producto) || clean(a?.idproducto);
    const bKey = clean(b?.producto) || clean(b?.idproducto);
    return aKey.localeCompare(bKey, 'es', { sensitivity: 'base' });
  });
}

function normalizePromo(row) {
  if (!row) return null;
  return {
    id: Number(row.id),
    idproducto: clean(row.idproducto),
    cod_barras: clean(row.cod_barras),
    producto: clean(row.producto),
    presentacion: clean(row.presentacion),
    laboratorio: clean(row.laboratorio),
    dto_peuma: clean(row.dto_peuma),
    precio: clean(row.precio),
    precio_descuento: clean(row.precio_descuento),
    is_active: Number(row.is_active ?? 0) === 1 ? 1 : 0,
    created_at: row.created_at || null,
    updated_at: row.updated_at || null
  };
}

function buildDraftFromRow(row) {
  if (!row) return { ...EMPTY_DRAFT };
  return {
    idproducto: clean(row.idproducto),
    cod_barras: clean(row.cod_barras),
    producto: clean(row.producto),
    presentacion: clean(row.presentacion),
    laboratorio: clean(row.laboratorio),
    dto_peuma: clean(row.dto_peuma),
    precio: formatAmountInput(row.precio),
    precio_descuento: formatAmountInput(row.precio_descuento),
    is_active: Number(row.is_active ?? 0) === 1 ? 1 : 0
  };
}

function inferDiscountedPrice(price, dto) {
  const rawPrice = parseAmount(price);
  const percent = Number(String(dto || '').replace('%', '').replace(',', '.'));
  if (!Number.isFinite(rawPrice) || rawPrice <= 0) return '';
  if (!Number.isFinite(percent) || percent < 0) return '';
  const discounted = rawPrice * (1 - percent / 100);
  return discounted > 0 ? discounted.toFixed(2) : '';
}

export default function BotFarmaciaPromociones() {
  const [promociones, setPromociones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [creating, setCreating] = useState(false);
  const [deletingIds, setDeletingIds] = useState({});
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogPageSize, setCatalogPageSize] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });

  const loadPromos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.promociones) ? json.promociones.map(normalizePromo).filter(Boolean) : [];
      setPromociones(sortPromos(list));
      if (selectedRow?.id) {
        const refreshed = list.find((r) => Number(r.id) === Number(selectedRow.id));
        if (refreshed) {
          setSelectedRow(refreshed);
          setDraft(buildDraftFromRow(refreshed));
        }
      }
    } catch (e) {
      setError(e.message || 'No se pudo cargar el catálogo de promociones.');
      setPromociones([]);
    } finally {
      setLoading(false);
    }
  }, [selectedRow?.id]);

  useEffect(() => {
    void loadPromos();
  }, [loadPromos]);

  const filteredPromos = useMemo(() => {
    const q = clean(search).toLowerCase();
    if (!q) return promociones;
    return promociones.filter((row) => {
      const haystack = [row.idproducto, row.producto, row.presentacion, row.laboratorio, row.cod_barras]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [promociones, search]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filteredPromos.length / pageSize));
  }, [filteredPromos.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    setCatalogPage(1);
  }, [catalogSearch, catalogPageSize]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filteredPromos;
    const start = (page - 1) * pageSize;
    return filteredPromos.slice(start, start + pageSize);
  }, [filteredPromos, page, pageSize]);

  const rangeStart = filteredPromos.length === 0 ? 0 : pageSize <= 0 ? 1 : (page - 1) * pageSize + 1;
  const rangeEnd = pageSize <= 0 ? filteredPromos.length : Math.min(page * pageSize, filteredPromos.length);
  const searchActive = Boolean(clean(search));
  const selectedId = Number(selectedRow?.id || 0);
  const totalDiscounted = useMemo(
    () => promociones.filter((p) => Number(String(p.precio_descuento || '').replace(',', '.')) > 0).length,
    [promociones]
  );
  const catalogFilteredResults = useMemo(() => {
    const q = clean(catalogSearch).toLowerCase();
    if (!q) return catalogResults;
    return catalogResults.filter((hit) => {
      const haystack = [
        hit.idproducto,
        hit.cod_barras,
        hit.producto,
        hit.presentacion,
        hit.laboratorio,
        hit.dto_peuma
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [catalogResults, catalogSearch]);
  const catalogTotalPages = useMemo(() => {
    if (catalogPageSize <= 0) return 1;
    return Math.max(1, Math.ceil(catalogFilteredResults.length / catalogPageSize));
  }, [catalogFilteredResults.length, catalogPageSize]);
  useEffect(() => {
    if (catalogPage > catalogTotalPages) setCatalogPage(1);
  }, [catalogPage, catalogTotalPages]);
  const catalogPageItems = useMemo(() => {
    if (catalogPageSize <= 0) return catalogFilteredResults;
    const start = (catalogPage - 1) * catalogPageSize;
    return catalogFilteredResults.slice(start, start + catalogPageSize);
  }, [catalogFilteredResults, catalogPage, catalogPageSize]);
  const catalogRangeStart = catalogFilteredResults.length === 0
    ? 0
    : catalogPageSize <= 0
      ? 1
      : (catalogPage - 1) * catalogPageSize + 1;
  const catalogRangeEnd = catalogPageSize <= 0
    ? catalogFilteredResults.length
    : Math.min(catalogPage * catalogPageSize, catalogFilteredResults.length);
  const catalogSearchActive = Boolean(clean(catalogSearch));
  const hasCatalogDraft = Boolean(selectedRow?.id || clean(draft.idproducto) || clean(draft.producto) || clean(draft.presentacion));
  const existingCatalogIds = useMemo(() => new Set(
    promociones
      .map((row) => clean(row.idproducto).toLowerCase())
      .filter(Boolean)
  ), [promociones]);

  function buildPromoPayload(source, overrides = {}) {
    const next = { ...source, ...overrides };
    const parsedPrice = parseAmount(next.precio);
    const parsedDiscount = parseAmount(next.precio_descuento);
    const inferredDiscount = inferDiscountedPrice(next.precio, next.dto_peuma);
    return {
      idproducto: clean(next.idproducto),
      cod_barras: clean(next.cod_barras),
      producto: clean(next.producto),
      presentacion: clean(next.presentacion),
      laboratorio: clean(next.laboratorio),
      dto_peuma: clean(next.dto_peuma),
      precio: Number.isFinite(parsedPrice) ? parsedPrice.toFixed(2) : clean(next.precio),
      precio_descuento: inferredDiscount || (Number.isFinite(parsedDiscount) ? parsedDiscount.toFixed(2) : clean(next.precio_descuento)),
      is_active: Number(String(next.is_active ?? 0).trim()) === 1 ? 1 : 0
    };
  }

  function updateField(field, value) {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'precio' || field === 'dto_peuma') {
        const inferred = inferDiscountedPrice(next.precio, next.dto_peuma);
        if (inferred) {
          next.precio_descuento = formatAmountInput(inferred);
        }
      }
      return next;
    });
  }

  function resetDraft() {
    setSelectedRow(null);
    setDraft({ ...EMPTY_DRAFT });
    setLookupQuery('');
    setCatalogQuery('');
    setCatalogResults([]);
    setCatalogSearch('');
    setCatalogPage(1);
  }

  function openRow(row) {
    setSelectedRow(row);
    setDraft(buildDraftFromRow(row));
    setLookupQuery(row.idproducto || '');
    setCatalogQuery('');
    setCatalogResults([]);
    setCatalogSearch('');
    setCatalogPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function applyCatalogHit(hit, queryLabel = '') {
    const normalized = normalizeBotCatalogHit(hit);
    if (!normalized) return;
    const inferredPrice = formatAmountInput(normalized?.precio);
    const dto = clean(normalized?.dto_peuma || '');
    const inferredDiscount = inferDiscountedPrice(inferredPrice, dto);
    setSelectedRow(null);
    setLookupQuery(queryLabel || normalized.idproducto || normalized.producto || '');
    setCatalogQuery(queryLabel || normalized.idproducto || normalized.producto || '');
    setDraft({
      idproducto: clean(normalized?.idproducto || queryLabel),
      cod_barras: clean(normalized?.cod_barras || ''),
      producto: clean(normalized?.producto || ''),
      presentacion: clean(normalized?.presentacion || ''),
      laboratorio: clean(normalized?.laboratorio || ''),
      dto_peuma: dto,
      precio: inferredPrice,
      precio_descuento: formatAmountInput(normalized?.precio_descuento || inferredDiscount),
      is_active: 0
    });
  }

  async function lookupCatalogQuery() {
    const query = clean(lookupQuery || draft.idproducto || draft.producto || draft.laboratorio);
    if (!query) return;
    setLookupLoading(true);
    setError('');
    try {
      const variants = buildSearchVariants(query);
      let hits = [];
      for (const variant of variants.length ? variants : [query]) {
        const catalog = await fetchAtencionBotProductos(variant, { limit: 24 });
        const nextHits = Array.isArray(catalog?.productos) ? catalog.productos : [];
        if (nextHits.length) {
          hits = nextHits;
          break;
        }
      }
      const normalizedHits = hits.map(normalizeBotCatalogHit).filter(Boolean);
      const match = pickBestCatalogHit(query, normalizedHits);
      if (!normalizedHits.length) {
        throw new Error('No encontré datos en el catálogo del bot.');
      }

      setCatalogResults(normalizedHits);
      setCatalogQuery(query);
      setCatalogSearch(query);
      setCatalogPage(1);

      if (match) {
        applyCatalogHit(match, query);
      } else {
        applyCatalogHit(normalizedHits[0], query);
      }
      showToast({
        title: 'Datos precargados',
        text: 'Tomé la información del catálogo del bot y la dejé lista para guardar.'
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'No pude precargar',
        text: e.message || 'Error buscando en el catálogo del bot.'
      });
    } finally {
      setLookupLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const hasRow = Boolean(selectedRow?.id);
    const payload = buildPromoPayload(draft);

    if (!payload.idproducto || !payload.producto) return;

    setCreating(true);
    try {
      const url = hasRow ? ENDPOINTS.update(selectedRow.id) : ENDPOINTS.create;
      const res = await fetch(`${API_BASE_URL}${url}`, {
        method: hasRow ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const saved = normalizePromo(json?.promocion);
      if (saved) {
        setPromociones((prev) => sortPromos(
          hasRow
            ? prev.map((row) => (Number(row.id) === Number(saved.id) ? saved : row))
            : [...prev.filter((row) => Number(row.id) !== Number(saved.id)), saved]
        ));
        setSelectedRow(saved);
        setDraft(buildDraftFromRow(saved));
      } else {
        await loadPromos();
      }
      showToast({
        title: hasRow ? 'Promoción actualizada' : 'Promoción creada',
        text: 'La ficha quedó guardada en la tabla.'
      });
    } catch (err) {
      if (/ya existe una promoción/i.test(String(err?.message || ''))) {
        await lookupCatalogQuery();
        return;
      }
      Swal.fire({
        icon: 'error',
        title: hasRow ? 'No se pudo actualizar' : 'No se pudo crear',
        text: err.message || 'Error guardando la promoción.'
      });
    } finally {
      setCreating(false);
    }
  }

  async function togglePromoActive(row, nextActive) {
    if (!row?.id) return;
    const payload = buildPromoPayload(row, { is_active: nextActive });
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(row.id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const saved = normalizePromo(json?.promocion);
      if (saved) {
        setPromociones((prev) => sortPromos(prev.map((item) => (Number(item.id) === Number(saved.id) ? saved : item))));
        if (Number(selectedRow?.id) === Number(saved.id)) {
          setSelectedRow(saved);
          setDraft(buildDraftFromRow(saved));
        }
      } else {
        await loadPromos();
      }
      showToast({
        title: nextActive ? 'Promoción activada' : 'Promoción desactivada',
        text: row.producto ? `${row.producto} cambió su estado.` : 'El estado quedó actualizado.'
      });
    } catch (err) {
      fireCrmAlert({
        icon: 'error',
        title: 'No se pudo actualizar el estado',
        text: err.message || 'Error guardando el switch.'
      });
    }
  }

  async function handleDelete(row) {
    const target = row || selectedRow;
    if (!target?.id) return;
    const confirm = await fireConfirmDialog({
      title: 'Eliminar promoción',
      html: buildCrmSwalBody({
        lead: 'Se eliminará la promoción seleccionada del catálogo.',
        details: [
          { label: 'Producto', value: target.producto || '—' },
          { label: 'Id producto', value: target.idproducto || '—' }
        ],
        items: ['La podés volver a cargar después', 'No afecta el catálogo base del bot']
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      width: 'min(440px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    const id = Number(target.id);
    setDeletingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      setPromociones((prev) => prev.filter((row) => Number(row.id) !== id));
      if (selectedId === id) resetDraft();
      showToast({
        title: 'Promoción eliminada',
        text: target.producto ? `${target.producto} salió del catálogo.` : 'La promoción fue quitada.'
      });
    } catch (err) {
      fireCrmAlert({
        icon: 'error',
        title: 'No se pudo eliminar',
        text: err.message || 'Error borrando la promoción.'
      });
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true" style={{ height: '100vh', margin: '15px' }}>
        <div className="crm-gd-spinner" aria-hidden="true" />
        <div className="crm-gd-loading__title">Cargando promociones</div>
        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Consultando catálogo de promociones…</p>
      </div>
    );
  }

  return (
    <section className="crm-amv-page crm-gd-page crm-pbody bfp-page">
      {error ? <div className="crm-amv-alert" role="alert">{error}</div> : null}

      <section className="crm-amv-hero-panel bfp-hero" aria-labelledby="bfp-title">
        <div className="crm-amv-hero-panel__head bfp-hero__head">
          <div className="crm-amv-hero-panel__brand">
            <div className="crm-amv-hero-panel__icon" aria-hidden="true">🏷️</div>
            <div className="crm-amv-hero-panel__titles">
              <p className="crm-amv-hero-panel__eyebrow">Catálogo promocional</p>
              <h2 id="bfp-title" className="crm-amv-hero-panel__title">Promociones por producto</h2>
            </div>
          </div>
          <div className="bfp-hero__stats">
            <div className="crm-amv-hero-stat">
              <span className="crm-amv-hero-stat__num">{promociones.length}</span>
              <span className="crm-amv-hero-stat__label">Total</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--visible">
              <span className="crm-amv-hero-stat__num">{totalDiscounted}</span>
              <span className="crm-amv-hero-stat__label">Con descuento</span>
            </div>
            <form className="bfp-lookup" onSubmit={(e) => { e.preventDefault(); void lookupCatalogQuery(); }}>
              <label className="bfp-lookup__field">
                <span className="bfp-lookup__label">Buscar medicamento</span>
                <input
                  className="bfp-lookup__input"
                  type="text"
                  value={lookupQuery}
                  onChange={(e) => setLookupQuery(e.target.value)}
                  placeholder="código plex, producto, barra o laboratorio"
                  autoComplete="off"
                />
              </label>
              <button
                type="submit"
                className="crm-amv-hero-panel__submit"
                disabled={lookupLoading || !clean(lookupQuery)}
              >
                {lookupLoading ? 'Buscando…' : 'Buscar'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <div className="bfp-grid">
        <article className="crm-amv-card bfp-card">
          <div className="bfp-card__head">
            <div>
              <h3 className="bfp-card__title">{selectedRow?.id ? 'Modificar promoción' : 'Agregar promoción'}</h3>
            </div>
          </div>

          {!hasCatalogDraft ? (
            <div className="crm-amv-state crm-amv-state--empty" role="status" style={{ minHeight: 360, marginTop: 8 }}>
              <span className="crm-amv-state__icon" aria-hidden="true">🏷️</span>
              <p className="crm-amv-state__title">Elegí un producto del catálogo</p>
              <p className="crm-amv-state__hint">
                Apenas seleccionés un resultado de botrecetasatencion, se precarga la ficha y aparecen los campos listos para guardar.
              </p>
              <button
                type="button"
                className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--ghost"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{ marginTop: 12 }}
              >
                Ir al buscador
              </button>
            </div>
          ) : (
            <form className="bfp-form" onSubmit={handleSubmit}>
              <div className="bfp-form__row">
                <label className="bfp-field">
                  <span className="bfp-field__label">ID Producto</span>
                  <input
                    className="bfp-input"
                    type="text"
                    value={draft.idproducto}
                    onChange={(e) => updateField('idproducto', e.target.value)}
                    placeholder="1039500079"
                    autoComplete="off"
                    disabled
                    style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      borderColor: '#cbd5e1',
                      cursor: 'not-allowed',
                      opacity: 1
                    }}
                  />
                </label>
                <label className="bfp-field">
                  <span className="bfp-field__label">Producto</span>
                  <input
                    className="bfp-input"
                    type="text"
                    value={draft.producto}
                    onChange={(e) => updateField('producto', e.target.value)}
                    placeholder="PROTEIN BAR"
                    autoComplete="off"
                    disabled
                    style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      borderColor: '#cbd5e1',
                      cursor: 'not-allowed',
                      opacity: 1
                    }}
                  />
                </label>

              </div>

              <div className="bfp-form__row">

                <label className="bfp-field">
                  <span className="bfp-field__label">Presentación</span>
                  <input
                    className="bfp-input"
                    type="text"
                    value={draft.presentacion}
                    onChange={(e) => updateField('presentacion', e.target.value)}
                    placeholder="frutilla/crema barra x 1"
                    autoComplete="off"
                    disabled
                    style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      borderColor: '#cbd5e1',
                      cursor: 'not-allowed',
                      opacity: 1
                    }}
                  />
                </label>
                <label className="bfp-field">
                  <span className="bfp-field__label">Descuento</span>

                  <div style={{ position: 'relative' }}>
                    <input
                      className="bfp-input"
                      type="text"
                      value={draft.dto_peuma}
                      onChange={(e) => updateField('dto_peuma', e.target.value)}
                      placeholder="30"
                      autoComplete="off"
                      style={{ paddingRight: 38 }}
                    />

                    <span
                      style={{
                        position: 'absolute',
                        right: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#64748b',
                        fontWeight: 700,
                        fontSize: 14,
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      %
                    </span>
                  </div>
                </label>
              </div>

              <div className="bfp-form__row">

              </div>

              <div className="bfp-form__row">
                <label className="bfp-field">
                  <span className="bfp-field__label">Precio</span>

                  <div style={{ position: 'relative' }}>
                    <input
                      className="bfp-input"
                      type="text"
                      inputMode="decimal"
                      value={draft.precio}
                      onChange={(e) => updateField('precio', e.target.value)}
                      placeholder="2200"
                      autoComplete="off"
                      style={{ paddingLeft: 40 }}
                    />

                    <span
                      style={{
                        position: 'absolute',
                        left: 6,
                        top: 6,
                        bottom: 6,
                        width: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8fafc',
                        borderRight: '1px solid #e2e8f0',
                        borderRadius: '10px 0 0 10px',
                        color: '#475569',
                        fontWeight: 800,
                        fontSize: 14,
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      $
                    </span>
                  </div>
                </label>

                <label className="bfp-field">
                  <span className="bfp-field__label">Precio con descuento</span>

                  <div style={{ position: 'relative' }}>
                    <input
                      className="bfp-input"
                      type="text"
                      inputMode="decimal"
                      value={draft.precio_descuento}
                      onChange={(e) => updateField('precio_descuento', e.target.value)}
                      placeholder="1540"
                      autoComplete="off"
                      style={{ paddingLeft: 40 }}
                    />

                    <span
                      style={{
                        position: 'absolute',
                        left: 6,
                        top: 6,
                        bottom: 6,
                        width: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8fafc',
                        borderRight: '1px solid #e2e8f0',
                        borderRadius: '10px 0 0 10px',
                        color: '#475569',
                        fontWeight: 800,
                        fontSize: 14,
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      $
                    </span>
                  </div>
                </label>
              </div>

              <div className="bfp-actions">
                <button type="button" className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--ghost" onClick={resetDraft} disabled={creating}>
                  {/* <UiIcon name="refresh" size={14} /> */}
                  Limpiar
                </button>
                <button
                  type="submit"
                  className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--primary"
                  disabled={creating || !clean(draft.idproducto) || !clean(draft.producto)}
                >
                  {/* <UiIcon name={selectedRow?.id ? 'edit' : 'plus'} size={14} /> */}
                  {creating ? 'Guardando…' : selectedRow?.id ? 'Guardar cambios' : 'Agregar promoción'}
                </button>
              </div>
            </form>
          )}
        </article>

        <article className="crm-amv-card bfp-card">
          {catalogResults.length > 0 ? (
            <aside className="bfp-results-drawer is-open" aria-label="Resultados del catálogo bot">
              <div className="bfp-results-drawer__shell">
                <div className="bfp-results-drawer__head">
                  <div className="bfp-results-drawer__head-copy">
                    <p className="bfp-card__eyebrow">Catálogo botrecetasatencion</p>
                    <h4 className="bfp-results__title">
                      {catalogQuery ? `Resultados para "${catalogQuery}"` : 'Resultados encontrados'}
                    </h4>
                    <div className="bfp-results__summary">
                      Mostrando <strong>{catalogRangeStart}</strong>–<strong>{catalogRangeEnd}</strong> de{' '}
                      <strong>{catalogFilteredResults.length}</strong>
                      {catalogSearchActive ? ` (filtrado de ${catalogResults.length})` : ''}
                    </div>
                  </div>
                  <div className="bfp-results__toolbar">
                    <label className="bfp-results__search">
                      <span className="bfp-results__search-label">Filtrar resultados</span>
                      <input
                        className="bfp-lookup__input"
                        type="search"
                        value={catalogSearch}
                        onChange={(e) => setCatalogSearch(e.target.value)}
                        placeholder="Producto, id, barra o laboratorio"
                      />
                    </label>
                    <label className="bfp-results__page-size">
                      <span className="bfp-results__search-label">Mostrar</span>
                      <select
                        className="crm-amv-select"
                        value={catalogPageSize <= 0 ? 'ALL' : String(catalogPageSize)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCatalogPageSize(v === 'ALL' ? 0 : Number(v));
                          setCatalogPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value="ALL">Todas</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--ghost"
                      onClick={() => {
                        setCatalogResults([]);
                        setCatalogQuery('');
                        setCatalogSearch('');
                        setCatalogPage(1);
                      }}
                    >
                      <UiIcon name="x" size={14} />
                      Ocultar
                    </button>
                  </div>
                </div>

                <div className="bfp-results-drawer__body">
                  {catalogSearchActive && catalogFilteredResults.length === 0 ? (
                    <div className="crm-amv-state crm-amv-state--no-match" role="status">
                      <span className="crm-amv-state__icon" aria-hidden="true">🔎</span>
                      <p className="crm-amv-state__title">Sin coincidencias</p>
                      <p className="crm-amv-state__hint">
                        Probá con otro texto dentro de los resultados cargados del bot.
                      </p>
                    </div>
                  ) : (
                    <div className="bfp-results__grid">
                      {catalogPageItems.map((hit, idx) => {
                        const hitId = clean(hit.idproducto).toLowerCase();
                        const isAdded = existingCatalogIds.has(hitId);
                        const selected = clean(draft.idproducto) === clean(hit.idproducto)
                          && clean(draft.producto).toLowerCase() === clean(hit.producto).toLowerCase();
                        return (
                          <button
                            key={`${hit.idproducto || hit.producto || idx}`}
                            type="button"
                            className={`bfp-result-card${selected ? ' is-selected' : ''}${isAdded ? ' is-disabled' : ''}`}
                            onClick={() => {
                              if (isAdded) return;
                              applyCatalogHit(hit, catalogQuery);
                            }}
                            disabled={isAdded}
                          >
                            {isAdded ? <span className="bfp-result-card__badge">Agregado</span> : null}
                            <span className="bfp-result-card__name">{hit.producto || 'Sin nombre'}</span>
                            <span className="bfp-result-card__meta">
                              {hit.presentacion || '—'}
                              {hit.laboratorio ? ` · ${hit.laboratorio}` : ''}
                            </span>
                            <span className="bfp-result-card__meta">
                              ID {hit.idproducto || '—'}{hit.cod_barras ? ` · Barra ${hit.cod_barras}` : ''}
                            </span>
                            <span className="bfp-result-card__foot">
                              <strong>${formatCurrency(hit.precio)}</strong>
                              {hit.precio_descuento ? <span>Desc. ${formatCurrency(hit.precio_descuento)}</span> : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bfp-results-drawer__foot">
                  <AmvTablePager
                    rangeStart={catalogRangeStart}
                    rangeEnd={catalogRangeEnd}
                    filteredCount={catalogFilteredResults.length}
                    totalCount={catalogResults.length}
                    searchActive={catalogSearchActive}
                    page={catalogPage}
                    totalPages={catalogTotalPages}
                    pageSize={catalogPageSize}
                    onPageChange={setCatalogPage}
                    entityLabel="resultados del catálogo"
                  />
                </div>
              </div>
            </aside>
          ) : null}

          {searchActive && filteredPromos.length === 0 ? (
            <div className="crm-amv-state crm-amv-state--no-match" role="status">
              <span className="crm-amv-state__icon" aria-hidden="true">🔎</span>
              <p className="crm-amv-state__title">Sin coincidencias</p>
              <p className="crm-amv-state__hint">Probá con otro idProducto, laboratorio o nombre de producto.</p>
            </div>
          ) : filteredPromos.length === 0 ? (
            <div className="crm-amv-state crm-amv-state--empty" role="status">
              <span className="crm-amv-state__icon" aria-hidden="true">🏷️</span>
              <p className="crm-amv-state__title">Sin promociones cargadas</p>
              <p className="crm-amv-state__hint">Usá la ficha de la izquierda para agregar la primera promoción.</p>
            </div>
          ) : (
            <>
              <div className="crm-amv-table-wrap">
                <table className="crm-amv-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Producto</th>
                      <th style={{ textAlign: 'left' }}>ID</th>
                      <th style={{ textAlign: 'right' }}>Sin Desc.</th>
                      <th style={{ textAlign: 'right' }}>Con Desc.</th>
                      <th style={{ textAlign: 'right' }}>Desc.</th>
                      <th style={{ textAlign: 'center' }}>Activo</th>
                      <th style={{ textAlign: 'center' }}>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pageItems.map((row) => {
                      const isDeleting = Boolean(deletingIds[row.id]);
                      const isSelected = Number(selectedRow?.id || 0) === Number(row.id);

                      return (
                        <tr key={row.id} className={isSelected ? 'is-visible' : ''}>
                          <td style={{ textAlign: 'left' }}>
                            <div className="crm-amv-table__doctor">
                              <span className="crm-amv-table__doctor-name">
                                {row.producto || '—'}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 11.5,
                                color: '#64748b',
                                marginTop: 3
                              }}
                            >
                              {row.presentacion || '—'}
                              {row.laboratorio ? ` · ${row.laboratorio}` : ''}
                            </div>
                          </td>

                          <td style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 700, color: '#334155' }}>
                              {row.idproducto || '—'}
                            </div>
                          </td>

                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            ${formatCurrency(row.precio)}
                          </td>

                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#0f2744',
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            ${formatCurrency(row.precio_descuento)}
                          </td>

                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color:
                                row.dto_peuma > 40
                                  ? '#dc2626' // Rojo
                                  : row.dto_peuma > 20
                                    ? '#f59e0b' // Naranja
                                    : '#16a34a', // Verde
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            {row.dto_peuma ? `${row.dto_peuma}%` : '—'}
                          </td>

                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className={`bfp-switch${Number(row.is_active || 0) === 1 ? ' is-on' : ''}`}
                              onClick={() => void togglePromoActive(row, Number(row.is_active || 0) === 1 ? 0 : 1)}
                              role="switch"
                              aria-checked={Number(row.is_active || 0) === 1}
                              aria-label={`${Number(row.is_active || 0) === 1 ? 'Desactivar' : 'Activar'} ${row.producto}`}
                            >
                              <span className="bfp-switch__track" aria-hidden="true">
                                <span className="bfp-switch__thumb" />
                              </span>
                            </button>
                          </td>

                          <td className="crm-amv-table__actions-cell">
                            <div className="crm-amv-row-actions">
                              <button
                                type="button"
                                className="crm-amv-row-action-btn"
                                onClick={() => openRow(row)}
                                title={`Editar ${row.producto}`}
                                aria-label={`Editar ${row.producto}`}
                              >
                                <UiIcon name="edit" size={14} />
                              </button>
                              <button
                                type="button"
                                className="crm-amv-row-action-btn crm-amv-row-action-btn--danger"
                                onClick={() => void handleDelete(row)}
                                disabled={isDeleting}
                                title={`Eliminar ${row.producto}`}
                                aria-label={`Eliminar ${row.producto}`}
                              >
                                <UiIcon name="trash" size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <AmvTablePager
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                filteredCount={filteredPromos.length}
                totalCount={promociones.length}
                searchActive={searchActive}
                page={page}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setPage}
                entityLabel="promociones"
              />
            </>
          )}
        </article>
      </div>
    </section>
  );
}
